/* eslint-disable react/react-in-jsx-scope */
"use client";

import { postContact } from "@/apis/queryFunctions/contact";
import { usePathname } from "next/navigation";
import { dictionaries } from "@/utils/i18n";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { X } from "lucide-react";
import contactStyles from "@/app/[locale]/lien-he/contact.module.css";

interface FormValues {
  ho_ten: string;
  dien_thoai: string;
  email: string;
  dia_chi: string;
  noi_dung: string;
}

const initialValues: FormValues = {
  ho_ten: "",
  dien_thoai: "",
  email: "",
  dia_chi: "",
  noi_dung: "",
};

export default function Contact({ fontSize }: { fontSize?: string }) {
  const pathname = usePathname();
  const locale = pathname.split("/")[1] || "vi";
  const dictionary = dictionaries[locale as "vi" | "en"] || dictionaries.vi;
  const [form, setForm] = useState<FormValues>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof FormValues, string>>>({});
  const [submittedStatus, setSubmittedStatus] = useState<"success" | "error" | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormValues, string>> = {};

    if (!form.ho_ten.trim()) {
      newErrors.ho_ten =
        locale === "vi"
          ? "Vui lòng nhập họ tên của quý khách"
          : "Please enter your full name";
    }
    if (!form.dien_thoai.trim()) {
      newErrors.dien_thoai =
        locale === "vi"
          ? "Vui lòng nhập số điện thoại"
          : "Please enter your phone number";
    } else {
      const vnPhoneRegex = /^0[35789]\d{8}$/;
      if (!vnPhoneRegex.test(form.dien_thoai.trim())) {
        newErrors.dien_thoai =
          locale === "vi"
            ? "Số điện thoại không hợp lệ (gồm 10 số, VD: 0912345678)"
            : "Invalid phone number (10 digits)";
      }
    }
    if (!form.email.trim()) {
      newErrors.email =
        locale === "vi"
          ? "Vui lòng nhập email để cửa hàng liên hệ với quý khách"
          : "Please enter your email";
    } else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) {
      newErrors.email =
        locale === "vi" ? "Email không hợp lệ" : "Invalid email address";
    }
    if (!form.noi_dung.trim()) {
      newErrors.noi_dung =
        locale === "vi" ? "Vui lòng nhập nội dung" : "Please enter your message";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const contactMutation = useMutation({
    mutationFn: postContact,
    onSuccess: (rs: any) => {
      console.log("Footer contact success:", rs);
      setSubmittedStatus("success");
      setForm(initialValues);
      setErrors({});
      setIsSubmitting(false);
    },
    onError: (err: any) => {
      console.error("Footer contact error:", err);
      setSubmittedStatus("error");
      setIsSubmitting(false);
    },
  });

  const handleChange = (field: keyof FormValues, value: string) => {
    let finalValue = value;
    if (field === "dien_thoai") {
      finalValue = value.replace(/\D/g, "").slice(0, 11);
    }
    setForm((prev) => ({ ...prev, [field]: finalValue }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setIsSubmitting(true);
      contactMutation.mutate({ ...form });
    }
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "6px 12px",
    marginTop: 3,
    border: "1px solid rgba(255, 255, 255, 0.8)",
    borderRadius: 7,
    fontSize: "12.5px",
    fontFamily: "inherit",
    outline: "none",
    boxSizing: "border-box",
    marginBottom: "3px",
    backgroundColor: "#fff",
    color: "#222",
  };

  const errorStyle: React.CSSProperties = {
    color: "#ffc9c9",
    fontSize: "11px",
    marginTop: -2,
    marginBottom: 6,
  };

  return (
    <div style={{ position: "relative", zIndex: 2, width: "100%" }}>
      <form onSubmit={handleSubmit} style={{ width: "100%" }}>
        <input
          style={inputStyle}
          placeholder={locale === "vi" ? "Họ tên *" : "Full name *"}
          value={form.ho_ten}
          onChange={(e) => handleChange("ho_ten", e.target.value)}
        />
        {errors.ho_ten && <div style={errorStyle}>{errors.ho_ten}</div>}

        <input
          style={inputStyle}
          type="tel"
          inputMode="numeric"
          maxLength={11}
          placeholder={locale === "vi" ? "Điện thoại *" : "Phone *"}
          value={form.dien_thoai}
          onChange={(e) => handleChange("dien_thoai", e.target.value)}
        />
        {errors.dien_thoai && <div style={errorStyle}>{errors.dien_thoai}</div>}

        <input
          style={inputStyle}
          type="email"
          placeholder="Email *"
          value={form.email}
          onChange={(e) => handleChange("email", e.target.value)}
        />
        {errors.email && <div style={errorStyle}>{errors.email}</div>}

        <input
          style={inputStyle}
          placeholder={locale === "vi" ? "Địa chỉ" : "Address"}
          value={form.dia_chi}
          onChange={(e) => handleChange("dia_chi", e.target.value)}
        />

        <textarea
          style={{ ...inputStyle, resize: "none", minHeight: 60 }}
          placeholder={locale === "vi" ? "Nội dung *" : "Message *"}
          value={form.noi_dung}
          onChange={(e) => handleChange("noi_dung", e.target.value)}
          rows={2}
        />
        {errors.noi_dung && <div style={errorStyle}>{errors.noi_dung}</div>}

        <button
          type="submit"
          disabled={isSubmitting}
          style={{
            marginTop: 6,
            height: 36,
            width: "100%",
            fontSize: "12.5px",
            fontWeight: "bold",
            textTransform: "uppercase",
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#f97316",
            color: "#fff",
            border: "none",
            borderRadius: 7,
            cursor: "pointer",
            transition: "background 0.2s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.background = "#FFCD00")}
          onMouseLeave={(e) => (e.currentTarget.style.background = "#f97316")}
        >
          {isSubmitting ? (locale === "vi" ? "ĐANG GỬI..." : "SENDING...") : (dictionary?.contact.form.button)} →
        </button>
      </form>

      {/* Signature Split Modal Popup */}
      {submittedStatus && (
        <div className={contactStyles.modalOverlay}>
          <div className={contactStyles.modalContent}>
            <div className={contactStyles.modalLeft}>
              <div className={contactStyles.emojiWrapper}>
                {submittedStatus === "success" ? (
                  <>
                    <svg className={contactStyles.svgCheckmark} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                      <path fill="none" stroke="#12854f" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" d="M14 27l7 7 17-17" />
                    </svg>
                    {/* Firework Sparkles */}
                    <span className={`${contactStyles.sparkle} ${contactStyles.sparkle1}`} />
                    <span className={`${contactStyles.sparkle} ${contactStyles.sparkle2}`} />
                    <span className={`${contactStyles.sparkle} ${contactStyles.sparkle3}`} />
                    <span className={`${contactStyles.sparkle} ${contactStyles.sparkle4}`} />
                    <span className={`${contactStyles.sparkle} ${contactStyles.sparkle5}`} />
                    <span className={`${contactStyles.sparkle} ${contactStyles.sparkle6}`} />
                    <span className={`${contactStyles.sparkle} ${contactStyles.sparkle7}`} />
                    <span className={`${contactStyles.sparkle} ${contactStyles.sparkle8}`} />
                  </>
                ) : (
                  <svg className={contactStyles.svgError} xmlns="http://www.w3.org/2000/svg" viewBox="0 0 52 52">
                    <path className={contactStyles.svgErrorPath1} fill="none" stroke="#e53e3e" strokeWidth="5" strokeLinecap="round" d="M16 16l20 20" />
                    <path className={contactStyles.svgErrorPath2} fill="none" stroke="#e53e3e" strokeWidth="5" strokeLinecap="round" d="M36 16L16 36" />
                  </svg>
                )}
              </div>
              <div className={contactStyles.leftBgDecoration} />
            </div>

            <div className={contactStyles.modalRight}>
              <button
                className={contactStyles.modalCloseBtn}
                onClick={() => setSubmittedStatus(null)}
                type="button"
              >
                <X size={16} />
              </button>

              <div>
                <h3 className={contactStyles.modalTitle}>
                  {submittedStatus === "success"
                    ? (locale === "vi" ? "Gửi thành công!" : "Well Done!")
                    : (locale === "vi" ? "Gửi thất bại!" : "Submission Failed!")}
                </h3>
                
                <p className={contactStyles.modalDesc}>
                  {submittedStatus === "success"
                    ? (locale === "vi"
                        ? "Cảm ơn bạn đã liên hệ với Viet Thien. Chúng tôi đã tiếp nhận thông tin và sẽ phản hồi lại bạn trong thời gian sớm nhất."
                        : "Thank you for contacting Viet Thien. We have received your information and will respond to you as soon as possible.")
                    : (locale === "vi"
                        ? "Đã xảy ra lỗi trong quá trình gửi. Vui lòng thử lại sau."
                        : "An error occurred during submission. Please try again later.")}
                </p>
              </div>

              <div className={contactStyles.modalFooter}>
                <button
                  type="button"
                  className={contactStyles.modalActionBtn}
                  onClick={() => setSubmittedStatus(null)}
                >
                  {locale === "vi" ? "Đóng" : "Close"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
